import { useState } from "react";
import { Check, GitBranch, GitMerge, Plus } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { BranchView } from "@/application/use-cases/b2c/build-b2c-board";
import { IterationPrLink } from "@/presentation/components/b2c/iteration-pr-link";
import { B2C_REPOS, type B2cRepo } from "@/infrastructure/supabase/b2c-board.types";

/**
 * The iteration's integration branches, one chip per repo (0144): repo, branch,
 * base when stacked, the PR, and whether it has merged. The iteration merges once
 * every chip has. Writers can add a repo, set a PR link, and — as a fallback when a
 * repo's merge workflow did not record it — mark a branch merged.
 */
interface IterationBranchesProps {
  branches: BranchView[];
  /** False once the iteration has merged. */
  canWrite: boolean;
  onSetPr: (repo: B2cRepo, url: string | null) => void;
  onAddRepo: (repo: B2cRepo) => void;
  onMarkMerged: (repo: B2cRepo) => void;
}

export function IterationBranches({
  branches,
  canWrite,
  onSetPr,
  onAddRepo,
  onMarkMerged,
}: IterationBranchesProps) {
  const [adding, setAdding] = useState(false);
  const missing = B2C_REPOS.filter((r) => !branches.some((b) => b.branch.repo === r));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {branches.map(({ branch, base }) => {
        const merged = branch.merged_at !== null;
        return (
          <span
            key={branch.repo}
            className={cn(
              "border-border flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-1.5 py-0.5 text-[11px]",
              merged && "border-[oklch(0.62_0.15_150_/_0.45)] bg-[oklch(0.62_0.15_150_/_0.06)]"
            )}
          >
            <span className="font-semibold">{branch.repo}</span>
            <span
              className="font-data text-muted-foreground flex items-center gap-1"
              title={`Cut from, and PRs into, ${base}`}
            >
              <GitBranch className="size-3" aria-hidden />
              {branch.branch}
              {base !== "main" ? <span>→ {base}</span> : null}
            </span>
            <IterationPrLink
              key={branch.pr_url ?? ""}
              url={branch.pr_url}
              canWrite={canWrite && !merged}
              label={`${branch.repo} pull request link`}
              onChange={(url) => onSetPr(branch.repo, url)}
            />
            {merged ? (
              <span className="flex items-center gap-1 font-semibold text-[oklch(0.5_0.14_150)]">
                <Check className="size-3" aria-hidden />
                merged
              </span>
            ) : canWrite && branch.pr_url ? (
              <button
                type="button"
                onClick={() => onMarkMerged(branch.repo)}
                title="Use when this repo's merge workflow did not record the merge"
                className="text-muted-foreground hover:text-primary flex items-center gap-1 rounded px-0.5"
              >
                <GitMerge className="size-3" aria-hidden />
                Mark merged
              </button>
            ) : null}
          </span>
        );
      })}

      {canWrite && missing.length > 0 ? (
        adding ? (
          <span className="flex items-center gap-1 text-[11px]">
            {missing.map((repo) => (
              <button
                key={repo}
                type="button"
                onClick={() => {
                  onAddRepo(repo);
                  setAdding(false);
                }}
                className="border-border hover:border-primary/50 hover:text-primary rounded-md border px-1.5 py-0.5"
              >
                {repo}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-muted-foreground px-1 hover:underline"
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-muted-foreground hover:text-primary flex items-center gap-1 rounded px-1 text-[11px]"
          >
            <Plus className="size-3" aria-hidden />
            Repo
          </button>
        )
      ) : null}
    </div>
  );
}
